import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, LogOut, Menu, X, MapPin, Network } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isDashboard =
    location.pathname.includes("/devices/") ||
    location.pathname.includes("/admin/devices/");

  if (isDashboard || location.pathname === "/login") return null;

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <header className="w-full bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white shadow-md relative">
      <nav className="w-full flex items-center justify-between px-6 py-5">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="/assets/provar_logo_border_2.png"
            alt="Provar Logo"
            className="h-14 w-auto"
          />
        </Link>

        {/* Desktop Menü */}
        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-white text-white hover:bg-white hover:text-red-700 transition-colors text-sm font-medium"
          >
            <Home className="w-5 h-5" />
            {t('header.home')}
          </Link>

          <Link
            to="/map"
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-white text-white hover:bg-white hover:text-red-700 transition-colors text-sm font-medium"
          >
            <MapPin className="w-5 h-5" />
            {t('header.map')}
          </Link>

          <Link
            to="/mimic"
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-white text-white hover:bg-white hover:text-red-700 transition-colors text-sm font-medium"
          >
            <Network className="w-5 h-5" />
            {t('header.mimic')}
          </Link>

          <Link
            to="/hierarchy"
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-white text-white hover:bg-white hover:text-red-700 transition-colors text-sm font-medium"
          >
            <Network className="w-5 h-5" />
            {t('header.topology')}
          </Link>


          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-white text-red-700 hover:bg-gray-100 transition-colors text-sm font-medium shadow-sm"
          >
            <LogOut className="w-5 h-5" />
            {t('header.logout')}
          </button>

          <div className="ml-2">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobil Hamburger */}
        <button
          className="md:hidden p-2 rounded hover:bg-red-600"
          onClick={toggleSidebar}
        >
          <Menu className="w-6 h-6" />
        </button>
      </nav>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white text-gray-800 shadow-lg z-50 transform transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <span className="font-semibold text-lg text-red-700">{t('header.menu')}</span>
          <button onClick={closeSidebar}>
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="flex flex-col p-4 gap-3">
          <Link
            to="/"
            onClick={closeSidebar}
            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 text-sm font-medium"
          >
            <Home className="w-5 h-5" />
            {t('header.home')}
          </Link>

          <Link
            to="/map"
            onClick={closeSidebar}
            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 text-sm font-medium"
          >
            <MapPin className="w-5 h-5" />
            {t('header.map')}
          </Link>

          <Link
            to="/mimic"
            onClick={closeSidebar}
            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 text-sm font-medium"
          >
            <Network className="w-5 h-5" />
            {t('header.mimic')}
          </Link>

          <Link
            to="/hierarchy"
            onClick={closeSidebar}
            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 text-sm font-medium"
          >
            <Network className="w-5 h-5" />
            {t('header.topology')}
          </Link>



          <button
            onClick={() => {
              handleLogout();
              closeSidebar();
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-red-600 hover:bg-gray-100 text-sm font-medium"
          >
            <LogOut className="w-5 h-5" />
            {t('header.logout')}
          </button>

          <div className="mt-4 flex justify-center">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
}
